import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Col, Popconfirm, Row, Table, Select, TablePaginationConfig, Card, Avatar, List, Badge, Drawer } from 'antd';
import { SuppliersApi, ProjectsApi } from 'api/api';
import { SupplierDto, CreateSupplierCommand, UpdateSupplierCommand, ProjectDto } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper, TopToolBox } from 'container/styled';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'utility/accessibility/hasPermission';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';
import { Link, useNavigate } from 'react-router-dom';
import { CustomTableStyleWrapper } from 'pages/style';
import SupplierForm from './SupplierForm';
import { AutoComplete } from '../../components/autoComplete/autoComplete';
import { filterKeys } from 'constants/constants';
import { useMediaQuery } from 'react-responsive';

const { Option } = Select;
const suppliersApi = new SuppliersApi();
const projectsApi = new ProjectsApi();

const PROJECT_FILTER_ALL = 0;
const PROJECT_FILTER_UNASSIGNED = -1;

type ModalState = {
  visible: boolean;
  editMode: boolean;
  selectedSupplier: SupplierDto | null;
};

export const SuppliersPage: React.FC = () => {
  const { t } = useTranslation();
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SupplierDto[]>([]);
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    editMode: false,
    selectedSupplier: null,
  });
  const [query, setQuery] = useState({
    searchTerm: '',
    pageNumber: 1,
    pageSize: 10,
    isActive: undefined as boolean | undefined,
    projectId: undefined as number | undefined,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectFilterValue, setProjectFilterValue] = useState<number>(PROJECT_FILTER_ALL);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await suppliersApi.apiSuppliersGet({
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
        searchTerm: query.searchTerm,
        isActive: query.isActive,
        projectId: query.projectId,
      });
      setData(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (err) {
      openNotificationWithIcon('error', t('suppliers:fetch-error', 'Failed to fetch suppliers'));
    } finally {
      setLoading(false);
    }
  }, [query, t]);

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({ pageSize: -1, isActive: true });
      setProjects(response.data.items || []);
    } catch (err) {
      openNotificationWithIcon('error', t('projects:fetch-error', 'Failed to fetch projects'));
    } finally {
      setProjectsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const columns = [
    {
      title: t('suppliers:name', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('suppliers:contact', 'Contact'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber: string | null) => <span>{phoneNumber || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('suppliers:email', 'Email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('suppliers:status', 'Status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span className={`status-text ${isActive ? 'active' : 'deactivate'}`}>
          {isActive ? t('global:active', 'Active') : t('global:inactive', 'Inactive')}
        </span>
      ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 150,
      render: (record: SupplierDto) => (
        <div className="table-actions">
          {hasPermission('suppliers:view') && (
            <Link to={`/suppliers/${record.id}`}>
              <Button className="btn-icon" type="default" shape="circle">
                <FeatherIcon icon="eye" size={16} />
              </Button>
            </Link>
          )}
          {hasPermission('suppliers:edit') && (
            <Button 
              type="primary"
              shape="circle"
              size="small"
              key="edit" 
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                handleEdit(record);
              }}
            >
              <FeatherIcon icon="edit" size={16} />
            </Button>
          )}
          {hasPermission('suppliers:delete') && (
            <Popconfirm
              title={t('suppliers:delete-confirm', 'Are you sure?')}
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDelete(record.id as number);
              }}
              onCancel={(e) => {
                e?.stopPropagation();
              }}
              okText={t('global:yes', 'Yes')}
              cancelText={t('global:no', 'No')}
              key="delete"
            >
              <Button
                danger
                type="primary"
                shape="circle"
                size="small"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                }}
              >
                <FeatherIcon icon="trash-2" size={16} />
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 300);
  };

  const handleStatusFilterChange = (value: string) => {
    setQuery(prev => ({
      ...prev,
      isActive: value === 'all' ? undefined : value === 'active',
      pageNumber: 1,
    }));
  };

  const handleProjectFilterChange = (value: number) => {
    setProjectFilterValue(value);
    setQuery(prev => ({
      ...prev,
      projectId: value === PROJECT_FILTER_ALL ? undefined : value,
      pageNumber: 1,
    }));
  };

  const resetModalState = () => {
    setModalState({
      visible: false,
      editMode: false,
      selectedSupplier: null,
    });
  };

  const handleModalSubmit = async (): Promise<void> => {
    resetModalState();
    await fetchSuppliers();
  };

  const handleEdit = (supplier: SupplierDto) => {
    setModalState({
      visible: true,
      editMode: true,
      selectedSupplier: supplier,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await suppliersApi.apiSuppliersIdDelete({ id });
      openNotificationWithIcon('success', t('suppliers:deleted', 'Supplier deleted successfully'));
      fetchSuppliers();
    } catch (err) {
      openNotificationWithIcon('error', t('suppliers:delete-error', 'Failed to delete supplier'));
    }
  };

  const handleViewDetails = (id: number) => {
    navigate(`/suppliers/${id}`);
  };

  const renderSupplierItem = (item: SupplierDto) => (
    <Card 
      className="supplier-card"
      hoverable 
      onClick={() => handleViewDetails(item.id as number)}
      style={{ 
        marginBottom: '12px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: 'none'
      }}
      bodyStyle={{ padding: '16px' }}
      actions={[
        hasPermission('suppliers:view') && (
          <Link to={`/suppliers/${item.id}`} key="view">
            <FeatherIcon icon="eye" size={16} />
          </Link>
        ),
        hasPermission('suppliers:edit') && (
          <FeatherIcon 
            icon="edit" 
            size={16} 
            key="edit" 
            onClick={(e: React.MouseEvent<SVGElement>) => {
              e.stopPropagation();
              handleEdit(item);
            }}
            style={{ color: '#1890ff' }}  // Primary blue color
          />
        ),
        hasPermission('suppliers:delete') && (
          <Popconfirm
            title={t('suppliers:delete-confirm', 'Are you sure?')}
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(item.id as number);
            }}
            onCancel={(e) => {
              e?.stopPropagation();
            }}
            okText={t('global:yes', 'Yes')}
            cancelText={t('global:no', 'No')}
            key="delete"
          >
            <FeatherIcon
              icon="trash-2" 
              size={16} 
              onClick={(e: React.MouseEvent<SVGElement>) => {
                e.stopPropagation();
              }}
              style={{ color: '#ff4d4f' }}
            />
          </Popconfirm>
        ),
      ].filter(Boolean)}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <Avatar 
          style={{ 
            backgroundColor: item.isActive ? '#52c41a' : '#f5222d',
            marginRight: '12px'
          }}
        >
          {item.name?.charAt(0).toUpperCase()}
        </Avatar>
        <div>
          <h3 style={{ margin: '0', fontSize: '16px' }}>{item.name}</h3>
          <Badge 
            status={item.isActive ? "success" : "error"} 
            text={item.isActive ? t('global:active', 'Active') : t('global:inactive', 'Inactive')} 
          />
        </div>
      </div>
      {item.phoneNumber && (
        <div style={{ marginBottom: '4px' }}>
          <FeatherIcon icon="phone" size={14} style={{ marginRight: '8px' }} />
          {item.phoneNumber}
        </div>
      )}
      {item.email && (
        <div>
          <FeatherIcon icon="mail" size={14} style={{ marginRight: '8px' }} />
          {item.email}
        </div>
      )}
    </Card>
  );

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('suppliers:title', 'Suppliers')}
          buttons={[
            <ExportButtonPageHeader
              key="1"
              title={t('suppliers:title', 'Suppliers')}
              data={data}
              columns={['name', 'phoneNumber', 'email', 'isActive']}
            />,
            !isMobile && hasPermission('suppliers:create') && (
              <Button
                key="2"
                type="primary"
                onClick={() => setModalState({ visible: true, editMode: false, selectedSupplier: null })}
              >
                <FeatherIcon icon="plus" size={16} />
                {t('suppliers:add-supplier', 'Add Supplier')}
              </Button>
            ),
          ].filter(Boolean)}
        />
      </CardToolbox>

      <Main>
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={isMobile ? 20 : 24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={handleSearch}
                        width="100%"
                        patterns
                        placeholder={t('suppliers:search', 'Search suppliers...')}
                      />
                    </div>
                  </Col>

                  {isMobile && (
                    <Col xs={4} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Button
                        onClick={() => setFiltersVisible(true)}
                        shape="circle"
                        icon={<FeatherIcon icon="filter" size={16} />}
                      />
                    </Col>
                  )}

                  {!isMobile && (
                    <Col lg={12} md={14} xs={24}>
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{t('projects:project-filter', 'Project:')}</span>
                          <Select
                            style={{ width: 220 }}
                            onChange={handleProjectFilterChange}
                            value={projectFilterValue}
                            loading={projectsLoading}
                          >
                            <Option value={PROJECT_FILTER_ALL}>{t('global:all', 'All')}</Option>
                            <Option value={PROJECT_FILTER_UNASSIGNED}>
                              {t('suppliers:project-filter-unassigned', 'Jo specifik për projekt')}
                            </Option>
                            {projects.map(project => (
                              <Option key={project.id} value={project.id as number}>
                                {project.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{t('suppliers:status', 'Status:')}</span>
                          <Select
                            style={{ width: 200 }}
                            onChange={handleStatusFilterChange}
                            defaultValue="all"
                          >
                            <Option value="all">{t('global:all', 'All')}</Option>
                            <Option value="active">{t('global:active', 'Active')}</Option>
                            <Option value="inactive">{t('global:inactive', 'Inactive')}</Option>
                          </Select>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </TopToolBox>
            </Col>
          </Row>
          <Row>
            {isMobile ? (
              <Col xs={24}>
                <List
                  loading={loading}
                  dataSource={data}
                  renderItem={renderSupplierItem}
                  pagination={{
                    current: query.pageNumber,
                    pageSize: query.pageSize,
                    total: pagination.total,
                    onChange: (page) => setQuery(prev => ({ ...prev, pageNumber: page })),
                    simple: true,
                  }}
                />
              </Col>
            ) : (
              <Cards headless>
                <CustomTableStyleWrapper>
                  <TableWrapper>
                    <Table
                      loading={loading}
                      dataSource={data}
                      columns={columns}
                      pagination={{
                        current: query.pageNumber,
                        pageSize: query.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 50, 100, 1000],
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                        onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                      }}
                    />
                  </TableWrapper>
                </CustomTableStyleWrapper>
              </Cards>
            )}
          </Row>
        </Cards>
      </Main>

      {modalState.visible && (
        <SupplierForm
          visible={modalState.visible}
          editMode={modalState.editMode}
          supplier={modalState.selectedSupplier}
          onCancel={() => resetModalState()}
          onSuccess={handleModalSubmit}
        />
      )}

      <Drawer
        title={t('global:filters', 'Filters')}
        placement="right"
        onClose={() => setFiltersVisible(false)}
        open={filtersVisible}
        width={isMobile ? "80%" : 450}
      >
        <div style={{ marginBottom: '15px' }}>
          <div style={{ marginBottom: '10px' }}>{t('projects:project-filter', 'Project')}</div>
          <Select
            style={{ width: '100%' }}
            onChange={handleProjectFilterChange}
            value={projectFilterValue}
            loading={projectsLoading}
          >
            <Option value={PROJECT_FILTER_ALL}>{t('global:all', 'All')}</Option>
            <Option value={PROJECT_FILTER_UNASSIGNED}>
              {t('suppliers:project-filter-unassigned', 'Jo specifik për projekt')}
            </Option>
            {projects.map(project => (
              <Option key={project.id} value={project.id as number}>
                {project.name}
              </Option>
            ))}
          </Select>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <div style={{ marginBottom: '10px' }}>{t('suppliers:status', 'Status')}</div>
          <Select
            style={{ width: '100%' }}
            onChange={handleStatusFilterChange}
            defaultValue="all"
          >
            <Option value="all">{t('global:all', 'All')}</Option>
            <Option value="active">{t('global:active', 'Active')}</Option>
            <Option value="inactive">{t('global:inactive', 'Inactive')}</Option>
          </Select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            type="primary"
            onClick={() => setFiltersVisible(false)}
          >
            {t('global:apply', 'Apply')}
          </Button>
        </div>
      </Drawer>

      {isMobile && hasPermission('suppliers:create') && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
        }}>
          <Button
            type="primary"
            shape="circle"
            size="large"
            onClick={() => setModalState({ visible: true, editMode: false, selectedSupplier: null })}
            style={{ width: '60px', height: '60px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}
          >
            <FeatherIcon icon="plus" size={24} />
          </Button>
          
          <Button
            type="default"
            shape="circle"
            size="large"
            onClick={() => navigate('/supplier-payments')}
            style={{ width: '60px', height: '60px', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}
          >
            <FeatherIcon icon="dollar-sign" size={24} />
          </Button>
        </div>
      )}
    </>
  );
};

export default SuppliersPage;